import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { catchError, Observable, switchMap } from 'rxjs';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../../common/automation-display.service';
import { CRMListsSelectorService } from '../../../../../data-services/crm-lists-selector.service';
import {
  CRMCompanyAddedToList,
  CRMCompanyRemovedFromList,
  CRMContactAddedToList,
  CRMContactRemovedFromList,
  CRMCustomObjectAddedToList,
  CRMCustomObjectRemovedFromList,
} from '../../../../component-loader/constants';
import { ObjectType } from '@galaxy/crm/static';

interface DisplayConfig {
  objectType: ObjectType;
  action: string;
  actionWithData: string;
  activity: string;
  description: string;
}

export const TaskConfigMap = new Map<string, DisplayConfig>([
  [
    CRMContactAddedToList,
    {
      objectType: 'Contact',
      action: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_ADDED_TO_LIST.ACTION',
      actionWithData: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_ADDED_TO_LIST.ACTION_WITH_DATA',
      activity: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_ADDED_TO_LIST.ACTIVITY',
      description: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_ADDED_TO_LIST.DESCRIPTION',
    },
  ],
  [
    CRMContactRemovedFromList,
    {
      objectType: 'Contact',
      action: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_REMOVED_FROM_LIST.ACTION',
      actionWithData: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_REMOVED_FROM_LIST.ACTION_WITH_DATA',
      activity: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_REMOVED_FROM_LIST.ACTIVITY',
      description: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_REMOVED_FROM_LIST.DESCRIPTION',
    },
  ],
  [
    CRMCompanyAddedToList,
    {
      objectType: 'Company',
      action: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_COMPANY_ADDED_TO_LIST.ACTION',
      actionWithData: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_COMPANY_ADDED_TO_LIST.ACTION_WITH_DATA',
      activity: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_COMPANY_ADDED_TO_LIST.ACTIVITY',
      description: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_COMPANY_ADDED_TO_LIST.DESCRIPTION',
    },
  ],
  [
    CRMCompanyRemovedFromList,
    {
      objectType: 'Company',
      action: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_COMPANY_REMOVED_FROM_LIST.ACTION',
      actionWithData: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_COMPANY_REMOVED_FROM_LIST.ACTION_WITH_DATA',
      activity: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_COMPANY_REMOVED_FROM_LIST.ACTIVITY',
      description: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_COMPANY_REMOVED_FROM_LIST.DESCRIPTION',
    },
  ],
  [
    CRMCustomObjectAddedToList,
    {
      objectType: 'CustomObject',
      action: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CUSTOM_OBJECT_ADDED_TO_LIST.ACTION',
      actionWithData: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CUSTOM_OBJECT_ADDED_TO_LIST.ACTION_WITH_DATA',
      activity: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CUSTOM_OBJECT_ADDED_TO_LIST.ACTIVITY',
      description: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CUSTOM_OBJECT_ADDED_TO_LIST.DESCRIPTION',
    },
  ],
  [
    CRMCustomObjectRemovedFromList,
    {
      objectType: 'CustomObject',
      action: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CUSTOM_OBJECT_REMOVED_FROM_LIST.ACTION',
      actionWithData: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CUSTOM_OBJECT_REMOVED_FROM_LIST.ACTION_WITH_DATA',
      activity: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CUSTOM_OBJECT_REMOVED_FROM_LIST.ACTIVITY',
      description: 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CUSTOM_OBJECT_REMOVED_FROM_LIST.DESCRIPTION',
    },
  ],
]);

@Injectable({
  providedIn: 'root',
})
export class CrmObjectListSelectorDisplayService extends AutomationDisplayService {
  private readonly listsSelectorService = inject(CRMListsSelectorService);

  constructor(protected readonly translateService: TranslateService) {
    super();
  }

  private getDisplayConfig(step: WorkflowStepInterface): DisplayConfig | undefined {
    return TaskConfigMap.get(step?.waitForTriggerTaskDefinitionId ?? step?.taskDefinitionId ?? CRMContactAddedToList);
  }

  /**
   * This is the text shown in the automation editor.
   */
  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    const data = JSON.parse(step?.data ?? '{}');
    const listId = data.user_provided_list_id ?? '';
    const displayConfig = this.getDisplayConfig(step);

    // Check if list ID is missing and set validation error
    if (!listId) {
      return this.nodeDisplayData(
        this.translateService.stream(
          displayConfig?.action ?? 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_ADDED_TO_LIST.ACTION',
        ),
        this.validationErrorsDetected(
          step,
          this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_ADDED_TO_LIST.CONFIG_ERROR'),
          true,
        ),
      );
    }

    return this.listsSelectorService.get(listId).pipe(
      switchMap((list) => {
        if (!list?.name) {
          return this.nodeDisplayData(
            this.translateService.stream(
              displayConfig?.action ?? 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_ADDED_TO_LIST.ACTION',
            ),
            this.validationErrorsDetected(
              step,
              this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_ADDED_TO_LIST.CONFIG_ERROR'),
              true,
            ),
          );
        }
        return this.nodeDisplayData(
          this.translateService.stream(
            displayConfig?.actionWithData ?? 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_ADDED_TO_LIST.ACTION_WITH_DATA',
            {
              listName: list.name,
            },
          ),
          this.noValidationErrorsDetected(),
        );
      }),
      catchError(() => {
        return this.nodeDisplayData(
          this.translateService.stream(
            displayConfig?.action ?? 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_ADDED_TO_LIST.ACTION',
          ),
          this.validationErrorsDetected(
            step,
            this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_ADDED_TO_LIST.CONFIG_ERROR'),
            true,
          ),
        );
      }),
    );
  }

  /**
   * This is the text that appears in the activity column in the activity table.
   * Each column has a title (big text) and body (small text).
   * Most, but not all, trigger activity entries have "Entered the automation" (or similar) as the title
   * and then a custom body.
   */
  getActivityColumn(_namespace: string, activity: ActivityInterface): ActivityTableEntry {
    const displayConfig = this.getDisplayConfig(activity?.workflowStep ?? {});
    return this.triggerActivityEntry(
      this.translateService.stream(
        displayConfig?.activity ?? 'AUTOMATIONS.EDITOR.TRIGGERS.CRM_CONTACT_ADDED_TO_LIST.ACTIVITY',
      ),
    );
  }
}
